import * as React from 'react';

import DeliverableSpecification from './deliverable-specification/DeliverableSpecification';

import { t } from 'utils/language';
import {
  DELIVERABLE_TYPE_BLOG,
  DELIVERABLE_TYPE_CONTENT_PRODUCTION,
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_OTHER,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  DELIVERABLE_TYPE_YOUTUBE_VIDEO
} from 'utils/deliverables';

import type { Deliverable } from 'types';

type Props = {
  deliverable: Deliverable
};

class DeliverableTabInformation extends React.PureComponent<Props> {
  private hasLandingPage(): boolean {
    const { deliverable } = this.props;
    const trackingLinks = deliverable.trackingLinks[0];

    return Boolean(trackingLinks);
  }

  getArrayKey(key: string): string {
    const { deliverable } = this.props;

    return `deliverable-specification-${deliverable.id}-${key}`;
  }

  renderLandingPageFields = (): JSX.Element | null => {
    const { deliverable } = this.props;
    const trackingLinks = deliverable.trackingLinks[0];

    return this.hasLandingPage() ? (
      <div className="deliverable-tab-information__landing-page-container">
        <div className="deliverable-tab-information__landing-page-fields">
          <DeliverableSpecification
            title={t('deliverable.landing_page_url_label')}
            type="link"
            text={trackingLinks?.shortUrl}
            link={trackingLinks?.shortUrl}
          />
        </div>
        <div className="deliverable-tab-information__landing-page-fields">
          <DeliverableSpecification
            title={t('deliverable.landing_page_description_label')}
            type="text"
            text={trackingLinks?.description}
          />
        </div>
      </div>
    ) : null;
  };

  renderLinks = (): JSX.Element => {
    const { deliverable } = this.props;
    const title = (this.hasLandingPage())
      ? t('deliverable.other_links_label')
      : t('deliverable.links_label');

    return (
      <DeliverableSpecification
        title={title}
        type="links"
        links={deliverable.links}
        text={t('deliverable.links_missing_label')}
      />
    );
  };

  renderInstagramPostSpecification = (): JSX.Element => {
    const { deliverable } = this.props;

    return (
      <React.Fragment>
        <DeliverableSpecification
          title={t('deliverable.description_label')}
          type="richtext"
          text={deliverable.description}
        />
        <DeliverableSpecification
          key={this.getArrayKey('hashtags')}
          title={t('deliverable.hashtags_label')}
          type="items"
          items={deliverable.hashtags}
          itemsPrefix="#"
        />
        <DeliverableSpecification
          key={this.getArrayKey('tags')}
          title={t('deliverable.tags_label')}
          type="items"
          items={deliverable.tags}
          itemsPrefix="@"
        />
        <DeliverableSpecification
          key={this.getArrayKey('mentions')}
          title={t('deliverable.mentions_label')}
          type="items"
          items={deliverable.mentions}
          itemsPrefix="@"
        />
        <DeliverableSpecification
          title={t('deliverable.location_label')}
          type="text"
          text={deliverable.location}
        />
        {this.renderLandingPageFields()}
        {this.renderLinks()}
      </React.Fragment>
    );
  };

  renderInstagramStorySpecification = (): JSX.Element => {
    const { deliverable } = this.props;

    return (
      <React.Fragment>
        <DeliverableSpecification
          title={t('deliverable.description_label')}
          type="richtext"
          text={deliverable.description}
        />
        <DeliverableSpecification
          key={this.getArrayKey('hashtags')}
          title={t('deliverable.hashtags_label')}
          type="items"
          items={deliverable.hashtags}
          itemsPrefix="#"
        />
        <DeliverableSpecification
          key={this.getArrayKey('mentions')}
          title={t('deliverable.mentions_label')}
          type="items"
          items={deliverable.mentions}
          itemsPrefix="@"
        />
        <DeliverableSpecification
          title={t('deliverable.location_label')}
          type="text"
          text={deliverable.location}
        />
        {this.renderLandingPageFields()}
        {this.renderLinks()}
      </React.Fragment>
    );
  };

  renderYoutubeVideoSpecification = (): JSX.Element => {
    const { deliverable } = this.props;

    return (
      <React.Fragment>
        <DeliverableSpecification
          title={t('deliverable.description_label')}
          type="richtext"
          text={deliverable.description}
        />
        <DeliverableSpecification
          key={this.getArrayKey('tags')}
          title={t('deliverable.tags_label')}
          type="items"
          items={deliverable.tags}
        />
        {this.renderLandingPageFields()}
        {this.renderLinks()}
      </React.Fragment>
    );
  };

  renderOtherSpecification = (): JSX.Element => {
    const { deliverable } = this.props;

    return (
      <React.Fragment>
        <DeliverableSpecification
          title={t('deliverable.description_label')}
          type="richtext"
          text={deliverable.description}
        />
        {this.renderLinks()}
      </React.Fragment>
    );
  };

  renderTikTokSpecification = (): JSX.Element => {
    const { deliverable } = this.props;

    return (
      <React.Fragment>
        <DeliverableSpecification
          title={t('deliverable.description_label')}
          type="richtext"
          text={deliverable.description}
        />
        <DeliverableSpecification
          key={this.getArrayKey('hashtags')}
          title={t('deliverable.hashtags_label')}
          type="items"
          items={deliverable.hashtags}
          itemsPrefix="#"
        />
        <DeliverableSpecification
          key={this.getArrayKey('mentions')}
          title={t('deliverable.mentions_label')}
          type="items"
          items={deliverable.mentions}
          itemsPrefix="@"
        />
        {this.renderLandingPageFields()}
        {this.renderLinks()}
      </React.Fragment>
    );
  };

  renderBlogSpecification = (): JSX.Element => {
    const { deliverable } = this.props;

    return (
      <React.Fragment>
        <DeliverableSpecification
          title={t('deliverable.description_label')}
          type="richtext"
          text={deliverable.description}
        />
        {this.renderLandingPageFields()}
        {this.renderLinks()}
      </React.Fragment>
    );
  };

  renderContentProductionSpecification = (): JSX.Element => {
    const { deliverable } = this.props;

    return (
      <DeliverableSpecification
        title={t('deliverable.description_label')}
        type="richtext"
        text={deliverable.description}
      />
    );
  };

  render(): React.ReactNode {
    const { deliverable } = this.props;

    return (
      <section className="deliverable-tab-information">
        <div className="deliverable-tab-information__specifications">
          {
            deliverable.deliverableType === DELIVERABLE_TYPE_INSTAGRAM_POST && (
              this.renderInstagramPostSpecification()
            )
          }
          {
            deliverable.deliverableType === DELIVERABLE_TYPE_INSTAGRAM_STORY && (
              this.renderInstagramStorySpecification()
            )
          }
          {
            deliverable.deliverableType === DELIVERABLE_TYPE_YOUTUBE_VIDEO && (
              this.renderYoutubeVideoSpecification()
            )
          }
          {
            deliverable.deliverableType === DELIVERABLE_TYPE_OTHER && (
              this.renderOtherSpecification()
            )
          }
          {
            deliverable.deliverableType === DELIVERABLE_TYPE_BLOG && (
              this.renderBlogSpecification()
            )
          }
          {
            deliverable.deliverableType === DELIVERABLE_TYPE_CONTENT_PRODUCTION && (
              this.renderContentProductionSpecification()
            )
          }
          {
            deliverable.deliverableType === DELIVERABLE_TYPE_TIKTOK_VIDEO && (
              this.renderTikTokSpecification()
            )
          }
        </div>
      </section>
    );
  }
}

export default DeliverableTabInformation;
